import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { Colors } from '@core/config';
import { useThemeStore } from '@core/theme/useThemeStore';
import { parseAuditLogNotes } from '../../utils/auditLogNotes.util';

interface ActivityLogItemProps {
  activityId: string;
  notes: string;
  createdAt: string;
}

const ActivityLogItem: React.FC<ActivityLogItemProps> = ({
  activityId,
  notes,
  createdAt,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showExpandToggle, setShowExpandToggle] = useState(false);
  const notesRef = useRef<HTMLParagraphElement>(null);
  const noteParts = useMemo(() => parseAuditLogNotes(notes), [notes]);
  const theme = useThemeStore();
  const linkColor = theme?.theme?.primaryBrandColor || Colors.LINK_BLUE;

  // useLayoutEffect is used over useEffect because we need to measure DOM elements.
  useLayoutEffect(() => {
    // Get the DOM paragraph element.
    const element = notesRef.current;
    if (!element || expanded) {
      return;
    }

    const updateTruncation = () => {
      const contentHeight = element.scrollHeight;
      const containerHeight = element.clientHeight;
      // If the content height is greater than the container height
      // then we should show the expand toggle icon.
      setShowExpandToggle(contentHeight > containerHeight);
    };

    updateTruncation();
    // Use a ResizeObserver to update the truncation state when the element size changes.
    // Important because it allows us to handle users changing their screen size. Whenever
    // they resize their browser window, the truncation state should be updated.
    const observer = new ResizeObserver(updateTruncation);
    observer.observe(element);
    return () => observer.disconnect();
  }, [notes, expanded]);

  return (
    <div className="flex min-w-0 items-start space-x-3">
      <div className="min-w-0 flex-1 overflow-x-hidden">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-start gap-1">
            <p
              ref={notesRef}
              id={`activity-notes-${activityId}`}
              className={`max-w-full min-w-0 flex-1 text-sm break-all text-gray-700 ${
                expanded ? '' : 'line-clamp-3'
              }`}
            >
              {noteParts.map((part, index) => {
                if (part.type === 'link') {
                  return (
                    <a
                      key={`${part.url}-${index}`}
                      href={part.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold hover:underline"
                      style={{ color: linkColor }}
                    >
                      {part.label}
                    </a>
                  );
                }

                return (
                  <React.Fragment key={`text-${index}`}>
                    {part.value}
                  </React.Fragment>
                );
              })}
            </p>
            {(showExpandToggle || expanded) && (
              <button
                type="button"
                className="mt-0.5 shrink-0 self-end rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                onClick={() => setExpanded((previous) => !previous)}
              >
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform ${
                    expanded ? 'rotate-180' : ''
                  }`}
                />
              </button>
            )}
          </div>
          <span className="shrink-0 text-xs whitespace-nowrap text-gray-500">
            {dayjs(parseInt(createdAt, 10)).fromNow()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogItem;
